import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryStub } from '../../utils';
import {
  BOMHardware,
  BOMSpecificationsData,
  BundleCost,
  BundleCostSearchRequest,
  MaterialGroup,
  MaterialGroupOption,
  PriceManagementService,
  PriceVersion,
  ProductPriceSheetData,
} from './';

// Singleton service instance
let priceManagementService: PriceManagementService | null = null;
const getService = () => {
  priceManagementService ??= new PriceManagementService();
  return priceManagementService;
};

/**
 * RTK Query API slice for price management operations
 */
export const priceManagementApi = createApi({
  reducerPath: 'priceManagementApi',
  baseQuery: baseQueryStub,
  tagTypes: [
    'BundleCost',
    'MaterialGroup',
    'MaterialGroupOption',
    'PriceVersion',
    'BOMHardware',
    'ProductPriceSheet',
    'BOMSpecifications',
  ],
  endpoints: builder => ({
    // Get bundle costs for a version with optional material group filter
    getBundleCosts: builder.query<BundleCost[], { versionId: number; materialGroup?: string }>({
      queryFn: async ({ versionId, materialGroup }) => {
        const service = getService();
        const data = await service.getBundleCosts(versionId, materialGroup);
        return { data };
      },
      providesTags: (result, _, { versionId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'BundleCost' as const, id })),
              { type: 'BundleCost', id: `version-${versionId}` },
            ]
          : [{ type: 'BundleCost', id: `version-${versionId}` }],
    }),

    // Search bundle costs with advanced filters
    searchBundleCosts: builder.query<BundleCost[], BundleCostSearchRequest>({
      queryFn: async request => {
        const service = getService();
        const data = await service.searchBundleCosts(request);
        return { data };
      },
      providesTags: (result, _, { versionId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'BundleCost' as const, id })),
              { type: 'BundleCost', id: `search-${versionId}` },
            ]
          : [{ type: 'BundleCost', id: `search-${versionId}` }],
    }),

    // Get material groups with item counts (for a specific version - filtering)
    getMaterialGroups: builder.query<MaterialGroup[], number>({
      queryFn: async versionId => {
        const service = getService();
        const data = await service.getMaterialGroups(versionId);
        return { data };
      },
      providesTags: (_, __, versionId) => [{ type: 'MaterialGroup', id: versionId }],
    }),

    // Get all material group options from the reference table (for Add modal dropdowns)
    getAllMaterialGroupOptions: builder.query<MaterialGroupOption[], void>({
      queryFn: async () => {
        const service = getService();
        const data = await service.getAllMaterialGroupOptions();
        return { data };
      },
      providesTags: [{ type: 'MaterialGroupOption', id: 'LIST' }],
    }),

    // Get price version by ID
    getVersionById: builder.query<PriceVersion, number>({
      queryFn: async versionId => {
        const service = getService();
        const data = await service.getVersionById(versionId);
        return { data };
      },
      providesTags: (_, __, id) => [{ type: 'PriceVersion', id }],
    }),

    // Get all versions for a manufacturer
    getVersionsByManufacturerId: builder.query<PriceVersion[], number>({
      queryFn: async manufacturerId => {
        const service = getService();
        const data = await service.getVersionsByManufacturerId(manufacturerId);
        return { data };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PriceVersion' as const, id })),
              { type: 'PriceVersion', id: 'LIST' },
            ]
          : [{ type: 'PriceVersion', id: 'LIST' }],
    }),

    // Create empty version
    createEmptyVersion: builder.mutation<PriceVersion, { manufacturerId: number }>({
      queryFn: async ({ manufacturerId }) => {
        const service = getService();
        const data = await service.createEmptyVersion(manufacturerId);
        return { data };
      },
      invalidatesTags: () => [{ type: 'PriceVersion', id: 'LIST' }],
    }),

    // Delete a draft version
    deleteVersion: builder.mutation<void, number>({
      queryFn: async versionId => {
        const service = getService();
        await service.deleteVersion(versionId);
        return { data: undefined };
      },
      invalidatesTags: () => [{ type: 'PriceVersion', id: 'LIST' }],
    }),

    // Create version from copy
    createVersionFromCopy: builder.mutation<
      PriceVersion,
      { sourceVersionId: number; changeDescription: string; effectiveDate: string }
    >({
      queryFn: async ({ sourceVersionId, changeDescription, effectiveDate }) => {
        const service = getService();
        const data = await service.createVersionFromCopy(
          sourceVersionId,
          changeDescription,
          effectiveDate
        );
        return { data };
      },
      invalidatesTags: () => [{ type: 'PriceVersion', id: 'LIST' }],
    }),

    // Get BOM hardware for a version
    getBOMHardware: builder.query<BOMHardware[], number>({
      queryFn: async versionId => {
        const service = getService();
        const data = await service.getBOMHardware(versionId);
        return { data };
      },
      providesTags: (result, _, versionId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'BOMHardware' as const, id })),
              { type: 'BOMHardware', id: `version-${versionId}` },
            ]
          : [{ type: 'BOMHardware', id: `version-${versionId}` }],
    }),

    // Get bundle cost by ID
    getBundleCostById: builder.query<BundleCost, number>({
      queryFn: async id => {
        const service = getService();
        const data = await service.getBundleCostById(id);
        return { data };
      },
      providesTags: (_, __, id) => [{ type: 'BundleCost', id }],
    }),

    // Create BOM hardware
    createBOMHardware: builder.mutation<
      BOMHardware,
      {
        versionId: number;
        hardwareType: string;
        description: string;
        costPerBox: number;
        itemsPerBox: number;
        quantity: number;
        notes?: string;
      }
    >({
      queryFn: async request => {
        const service = getService();
        const data = await service.createBOMHardware(request);
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BOMHardware', id: `version-${versionId}` },
      ],
    }),

    // Update BOM hardware
    updateBOMHardware: builder.mutation<
      BOMHardware,
      {
        id: number;
        versionId: number;
        hardwareType: string;
        description: string;
        costPerBox: number;
        itemsPerBox: number;
        quantity: number;
        notes?: string;
      }
    >({
      queryFn: async ({
        id,
        hardwareType,
        description,
        costPerBox,
        itemsPerBox,
        quantity,
        notes,
      }) => {
        const service = getService();
        const data = await service.updateBOMHardware(id, {
          hardwareType,
          description,
          costPerBox,
          itemsPerBox,
          quantity,
          notes,
        });
        return { data };
      },
      invalidatesTags: (_, __, { id, versionId }) => [
        { type: 'BOMHardware', id },
        { type: 'BOMHardware', id: `version-${versionId}` },
      ],
    }),

    // Delete BOM hardware
    deleteBOMHardware: builder.mutation<void, { id: number; versionId: number }>({
      queryFn: async ({ id }) => {
        const service = getService();
        await service.deleteBOMHardware(id);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { id, versionId }) => [
        { type: 'BOMHardware', id },
        { type: 'BOMHardware', id: `version-${versionId}` },
      ],
    }),

    // Create bundle cost
    createBundleCost: builder.mutation<
      BundleCost,
      {
        versionId: number;
        materialGroup: string;
        size: string;
        bundleCost: number;
        sticksPerBundle?: number;
        feetPerStick?: number;
        sheetsPerBundle?: number;
        itemsPerSheet?: number;
        itemsPerStick?: number;
        notes?: string;
      }
    >({
      queryFn: async request => {
        const service = getService();
        const data = await service.createBundleCost(request);
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BundleCost', id: `version-${versionId}` },
        { type: 'MaterialGroup', id: versionId },
      ],
    }),

    // Update bundle cost
    updateBundleCost: builder.mutation<
      BundleCost,
      {
        id: number;
        versionId: number;
        bundleCost: number;
        sticksPerBundle?: number;
        feetPerStick?: number;
        sheetsPerBundle?: number;
        itemsPerSheet?: number;
        itemsPerStick?: number;
        notes?: string;
        rowVersion?: number[];
      }
    >({
      queryFn: async ({
        id,
        bundleCost,
        sticksPerBundle,
        feetPerStick,
        sheetsPerBundle,
        itemsPerSheet,
        itemsPerStick,
        notes,
        rowVersion,
      }) => {
        const service = getService();
        const data = await service.updateBundleCost(id, {
          bundleCost,
          sticksPerBundle,
          feetPerStick,
          sheetsPerBundle,
          itemsPerSheet,
          itemsPerStick,
          notes,
          rowVersion,
        });
        return { data };
      },
      invalidatesTags: (_, __, { id, versionId }) => [
        { type: 'BundleCost', id },
        { type: 'BundleCost', id: `version-${versionId}` },
        { type: 'MaterialGroup', id: versionId },
      ],
    }),

    // Delete bundle cost
    deleteBundleCost: builder.mutation<void, { id: number; versionId: number }>({
      queryFn: async ({ id }) => {
        const service = getService();
        await service.deleteBundleCost(id);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { id, versionId }) => [
        { type: 'BundleCost', id },
        { type: 'BundleCost', id: `version-${versionId}` },
        { type: 'MaterialGroup', id: versionId },
      ],
    }),

    // Validate size uniqueness
    validateSizeUniqueness: builder.query<
      boolean,
      { versionId: number; materialGroup: string; size: string; excludeId?: number }
    >({
      queryFn: async request => {
        const service = getService();
        const data = await service.validateSizeUniqueness(request);
        return { data };
      },
    }),

    // Publish a draft version
    publishVersion: builder.mutation<
      PriceVersion,
      { versionId: number; rowVersion: number[]; changeDescription: string }
    >({
      queryFn: async ({ versionId, rowVersion, changeDescription }) => {
        const service = getService();
        const data = await service.publishVersion(versionId, rowVersion, changeDescription);
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'PriceVersion', id: versionId },
        { type: 'PriceVersion', id: 'LIST' },
      ],
    }),

    // Bulk update bundle costs by percentage
    bulkUpdateBundleCostsByPercentage: builder.mutation<
      { updatedCount: number; cappedCount: number; message: string },
      { versionId: number; percentageChange: number; materialGroup?: string }
    >({
      queryFn: async ({ versionId, percentageChange, materialGroup }) => {
        const service = getService();
        const data = await service.bulkUpdateBundleCostsByPercentage(
          versionId,
          percentageChange,
          materialGroup
        );
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BundleCost', id: `version-${versionId}` },
      ],
    }),

    // Bulk update bundle costs by fixed amount
    bulkUpdateBundleCostsByFixedAmount: builder.mutation<
      { updatedCount: number; cappedCount: number; message: string },
      { versionId: number; fixedAmount: number; materialGroup?: string }
    >({
      queryFn: async ({ versionId, fixedAmount, materialGroup }) => {
        const service = getService();
        const data = await service.bulkUpdateBundleCostsByFixedAmount(
          versionId,
          fixedAmount,
          materialGroup
        );
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BundleCost', id: `version-${versionId}` },
      ],
    }),

    // Bulk update BOM hardware by percentage
    bulkUpdateBOMHardwareByPercentage: builder.mutation<
      { updatedCount: number; cappedCount: number; message: string },
      { versionId: number; percentageChange: number }
    >({
      queryFn: async ({ versionId, percentageChange }) => {
        const service = getService();
        const data = await service.bulkUpdateBOMHardwareByPercentage(versionId, percentageChange);
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BOMHardware', id: `version-${versionId}` },
      ],
    }),

    // Bulk update BOM hardware by fixed amount
    bulkUpdateBOMHardwareByFixedAmount: builder.mutation<
      { updatedCount: number; cappedCount: number; message: string },
      { versionId: number; fixedAmount: number }
    >({
      queryFn: async ({ versionId, fixedAmount }) => {
        const service = getService();
        const data = await service.bulkUpdateBOMHardwareByFixedAmount(versionId, fixedAmount);
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [
        { type: 'BOMHardware', id: `version-${versionId}` },
      ],
    }),

    // Get product price sheet for a manufacturer
    getProductPriceSheet: builder.query<
      ProductPriceSheetData,
      { manufacturerId: number; riskLevel?: string; category?: string }
    >({
      queryFn: async ({ manufacturerId, riskLevel, category }) => {
        const service = getService();
        const data = await service.getProductPriceSheet(manufacturerId, riskLevel, category);
        return { data };
      },
      providesTags: (_, __, { manufacturerId }) => [
        { type: 'ProductPriceSheet', id: manufacturerId },
      ],
    }),

    // Get read-only BOM Specifications for a manufacturer
    getBOMSpecifications: builder.query<
      BOMSpecificationsData,
      { manufacturerId: number; riskLevel?: string; productType?: string }
    >({
      queryFn: async ({ manufacturerId, riskLevel, productType }) => {
        const service = getService();
        const data = await service.getBOMSpecifications(manufacturerId, riskLevel, productType);
        return { data };
      },
      providesTags: (_, __, { manufacturerId }) => [
        { type: 'BOMSpecifications', id: manufacturerId },
      ],
    }),

    // Update price settings for a version
    updatePriceSettings: builder.mutation<
      PriceVersion,
      { versionId: number; fullyBurdenedLaborRate: number; productionFeetPerHour: number }
    >({
      queryFn: async ({ versionId, fullyBurdenedLaborRate, productionFeetPerHour }) => {
        const service = getService();
        const data = await service.updatePriceSettings(
          versionId,
          fullyBurdenedLaborRate,
          productionFeetPerHour
        );
        return { data };
      },
      invalidatesTags: (_, __, { versionId }) => [{ type: 'PriceVersion', id: versionId }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetBundleCostsQuery,
  useLazyGetBundleCostsQuery,
  useSearchBundleCostsQuery,
  useLazySearchBundleCostsQuery,
  useGetMaterialGroupsQuery,
  useLazyGetMaterialGroupsQuery,
  useGetAllMaterialGroupOptionsQuery,
  useGetVersionByIdQuery,
  useLazyGetVersionByIdQuery,
  useGetVersionsByManufacturerIdQuery,
  useLazyGetVersionsByManufacturerIdQuery,
  useCreateEmptyVersionMutation,
  useDeleteVersionMutation,
  useCreateVersionFromCopyMutation,
  useGetBOMHardwareQuery,
  useLazyGetBOMHardwareQuery,
  useGetBundleCostByIdQuery,
  useLazyGetBundleCostByIdQuery,
  useCreateBundleCostMutation,
  useUpdateBundleCostMutation,
  useDeleteBundleCostMutation,
  useCreateBOMHardwareMutation,
  useUpdateBOMHardwareMutation,
  useDeleteBOMHardwareMutation,
  useLazyValidateSizeUniquenessQuery,
  usePublishVersionMutation,
  useBulkUpdateBundleCostsByPercentageMutation,
  useBulkUpdateBundleCostsByFixedAmountMutation,
  useBulkUpdateBOMHardwareByPercentageMutation,
  useBulkUpdateBOMHardwareByFixedAmountMutation,
  useGetProductPriceSheetQuery,
  useLazyGetProductPriceSheetQuery,
  useGetBOMSpecificationsQuery,
} = priceManagementApi;

export const useUpdatePriceSettingsMutation = priceManagementApi.useUpdatePriceSettingsMutation;
